import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { eventBus } from '@atc/cai-event-bus';
import { UDS_SET_DEALTYPE_REQUEST, debugWallet, UDS_SET_DEALTYPE_COMPLETED } from 'wallet-manager';
import { ndsNavigationClick } from 'reaxl-analytics-handlers';
import { sendClick } from 'reaxl-analytics';
import _capitalize from 'lodash/capitalize';

/**
 * Hook that handles external deal type changes via event bus. Updates active tab and wallet payments
 * info when `UDS_SET_DEALTYPE_REQUEST` is received, then emits `UDS_SET_DEALTYPE_COMPLETED`.
 *
 * @param {string[]} supportedDealTypes - Array of supported deal types (e.g. 'finance', 'lease', 'cash')
 * @param {Function} setActiveTab - Function to update the active deal type tab
 * @param {Function} updatePaymentsInfo - Function to update the payments info in the wallet
 * @param {Object} paymentsInfo - Current payments info object from the wallet
 * @returns {void}
 */
const useExternalDealTypeChange = function (supportedDealTypes, setActiveTab, updatePaymentsInfo, paymentsDuck) {
  let analyticsData = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
  let handleNDSClick = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : () => {};
  const {
    isNDSPenciled,
    listingId,
    price,
    offerPrice
  } = analyticsData;

  // Get the latest paymentsInfo from the store
  const paymentsInfo = useSelector(state => paymentsDuck?.selectors?.getPaymentsInfo(state)) || {};
  useEffect(() => {
    const handleDealTypeChange = _ref => {
      let {
        dealType: requestedDealType,
        placement = 'nds',
        event
      } = _ref;
      if (requestedDealType && supportedDealTypes.includes(requestedDealType)) {
        setActiveTab(requestedDealType);

        // update wallet
        updatePaymentsInfo({
          ...paymentsInfo,
          dealType: requestedDealType,
          interactions: {
            ...paymentsInfo.interactions,
            ...(!paymentsInfo.interactions?.paymentEmphasis && {
              paymentEmphasis: true
            })
          }
        });
        const userModified = 'DEAL_TAB_CHANGED';
        const eventLabel = `${requestedDealType}-tab${placement === 'deal-type-tabs' ? '-deal-incentive' : ''}`;
        sendClick(ndsNavigationClick, event, {
          cmp: eventLabel,
          eventResult: eventLabel,
          listingId,
          walletData: {
            paymentsInfo,
            userModified,
            price,
            offerPrice
          },
          key: requestedDealType
        });
        handleNDSClick(event, userModified, _capitalize(requestedDealType), requestedDealType);
        debugWallet('UDS', 'PaymentCalcResponse:Update', requestedDealType);
        eventBus.emit(UDS_SET_DEALTYPE_COMPLETED);
      }
    };
    eventBus.on(UDS_SET_DEALTYPE_REQUEST, handleDealTypeChange);
    return () => eventBus.off(UDS_SET_DEALTYPE_REQUEST, handleDealTypeChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportedDealTypes, setActiveTab, updatePaymentsInfo, paymentsInfo, isNDSPenciled, listingId, price, offerPrice]);
};
export { useExternalDealTypeChange as default };
